import { useEffect, useState } from 'react';
import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useModules } from '../context/ModuleContext';
import { getSuspensionStatus, setSuspensionModalCallback } from '../utils/axiosInterceptor';
import { fetchTenantInfo, getTenant } from '../utils/tenantDetection';
import SuspensionModal from './common/SuspensionModal';
import './Dashboard.css';

const Dashboard = () => {
  const { user, logout, loading: authLoading } = useAuth();
  const { isModuleEnabled, loading: modulesLoading, refreshModules } = useModules();
  const navigate = useNavigate();
  const location = useLocation();
  const [tenant, setTenant] = useState(null);
  const [suspensionModal, setSuspensionModal] = useState({
    isOpen: false,
    status: null,
    message: '',
    endDate: null
  });

  useEffect(() => {
    // Set callback for axios interceptor to show suspension modal
    setSuspensionModalCallback((modalData) => {
      setSuspensionModal(modalData);
    });
    
    // Check for persisted suspension status on mount
    const persistedStatus = getSuspensionStatus();
    if (persistedStatus) {
      setSuspensionModal({
        isOpen: true,
        status: persistedStatus.status,
        message: persistedStatus.message,
        endDate: persistedStatus.endDate
      });
    }

    // Load tenant information
    const loadTenant = async () => {
      // First try to get from localStorage
      const storedTenant = getTenant();
      if (storedTenant) {
        setTenant(storedTenant);
      } else if (user?.tenantId) {
        // If user has tenantId but no stored tenant, fetch it
        try {
          const tenantInfo = await fetchTenantInfo();
          if (tenantInfo) {
            setTenant(tenantInfo);
          }
        } catch (error) {
          console.error('[Dashboard] Error fetching tenant info:', error);
        }
      }
    };

    loadTenant();

    // Refresh modules periodically to catch changes from SaaS admin (every 60 seconds)
    // Only refresh when page is visible and no modals are likely open
    // Only set interval if user has tenantId
    let refreshInterval = null;
    if (user?.tenantId) {
      refreshInterval = setInterval(() => {
        // Skip refresh if page is not visible (user switched tabs/windows)
        if (document.hidden) {
          return;
        }
        
        // Skip refresh if any modal is open (check for common modal classes)
        const hasOpenModal = document.querySelector('.modal-overlay:not([style*="display: none"]), .product-modal-overlay, [class*="modal"][class*="show"]');
        if (hasOpenModal) {
          return;
        }
        
        refreshModules(false);
      }, 60000); // Increased from 30s to 60s to reduce frequency
    }

    return () => {
      if (refreshInterval) {
        clearInterval(refreshInterval);
      }
    };
  }, [user?.tenantId, refreshModules]); // Only depend on tenantId, not entire user object

  // Check suspension status on route changes
  useEffect(() => {
    const persistedStatus = getSuspensionStatus();
    setSuspensionModal(prev => {
      // Only update if status actually changed to prevent unnecessary re-renders
      if (persistedStatus && !prev.isOpen) {
        return {
          ...prev,
          isOpen: true,
          status: persistedStatus.status,
          message: persistedStatus.message,
          endDate: persistedStatus.endDate
        };
      } else if (!persistedStatus && prev.isOpen) {
        // Clear modal if suspension status was cleared
        return {
          ...prev,
          isOpen: false,
          status: null,
          message: '',
          endDate: null
        };
      }
      // No change needed
      return prev;
    });
  }, [location.pathname]); // Only run on pathname change

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const handleCloseSuspensionModal = () => {
    // Do nothing - modal should not be dismissible when suspended
    // Only clear when account is reactivated (handled by backend)
  };

  const isActive = (path) => {
    return location.pathname === path ? 'active' : '';
  };

  if (authLoading || modulesLoading) {
    return (
      <div className="dashboard">
        <div className="main-content" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard">
      <nav className="sidebar">
        <div className="sidebar-header">
          <h2>Admin Panel</h2>
        </div>
        <div className="sidebar-user">
          {tenant?.name ? (
            <p>Welcome, {tenant.name}</p>
          ) : (
            <p>Welcome, {user?.username || 'Admin'}</p>
          )}
          {tenant && (
            <p style={{ fontSize: '12px', color: '#a0a0a0', marginTop: '2px' }}>
              {tenant.subdomain || tenant.name}
            </p>
          )}
          {user?.tenantId && (
            <button
              onClick={refreshModules}
              className="btn-refresh-modules"
              title="Refresh modules"
              style={{
                marginTop: '5px',
                padding: '4px 8px',
                fontSize: '11px',
                background: '#3399cc',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer'
              }}
            >
              🔄 Refresh
            </button>
          )}
        </div>
        <ul className="sidebar-menu">
          <li>
            <Link to="/" className={isActive('/') || location.pathname === '/' ? 'active' : ''}>
              📊 Dashboard
            </Link>
          </li>
          {isModuleEnabled('ORDERS') && (
            <li>
              <Link to="/orders" className={isActive('/orders')}>
                🛒 Orders
              </Link>
            </li>
          )}
          {isModuleEnabled('COUPONS') && (
            <li>
              <Link to="/coupons" className={isActive('/coupons')}>
                🎫 Coupon Manager
              </Link>
            </li>
          )}
          {/* <li>
            <Link to="/upload" className={isActive('/upload')}>
              📤 Upload Images
            </Link>
          </li> */}
          {isModuleEnabled('CATEGORIES') && (
            <li>
              <Link to="/categories" className={isActive('/categories')}>
                📁 Category Manager
              </Link>
            </li>
          )}
          {isModuleEnabled('PRODUCTS') && (
            <>
              <li>
                <Link to="/products" className={isActive('/products')}>
                  📦 Product Manager
                </Link>
              </li>
              <li>
                <Link to="/attributes" className={isActive('/attributes')}>
                  🏷️ Attribute Manager
                </Link>
              </li>
            </>
          )}
          {isModuleEnabled('BANNERS') && (
            <li>
              <Link to="/banners" className={isActive('/banners')}>
                🖼️ Banner Manager
              </Link>
            </li>
          )}
          {isModuleEnabled('BLOGS') && (
            <li>
              <Link to="/blogs" className={isActive('/blogs')}>
                📝 Blog Manager
              </Link>
            </li>
          )}
          {isModuleEnabled('WORKSHOPS') && (
            <li>
              <Link to="/workshops" className={isActive('/workshops')}>
                🎓 Workshop Manager
              </Link>
            </li>
          )}
          {/* {isModuleEnabled('SEO') && (
            <li>
              <Link to="/seo" className={isActive('/seo')}>
                🔍 SEO Manager
              </Link>
            </li>
          )} */}
          {isModuleEnabled('REVIEWS') && (
            <li>
              <Link to="/reviews" className={isActive('/reviews')}>
                ⭐ Reviews Manager
              </Link>
            </li>
          )}
          {isModuleEnabled('AUTOMATION') && (
            <li>
              <Link to="/automation" className={isActive('/automation')}>
                🤖 AI Automation
              </Link>
            </li>
          )}
          {isModuleEnabled('AI_CHATBOT') && (
            <li>
              <Link to="/ai-config" className={isActive('/ai-config')}>
                🔐 AI Configuration
              </Link>
            </li>
          )}
          {isModuleEnabled('WORKFLOWS') && (
            <li>
              <Link to="/workflows" className={isActive('/workflows')}>
                🔄 Workflow Orchestrator
              </Link>
            </li>
          )}
          <li>
            <Link to="/settings" className={isActive('/settings')}>
              ⚙️ Settings
            </Link>
          </li>
          {/* <li>
            <Link to="/users" className={isActive('/users')}>
              👥 User Management
            </Link>
          </li> */}
          {/* <li>
            <Link to="/billing" className={isActive('/billing')}>
              💳 Billing & Subscription
            </Link>
          </li> */}
        </ul>
        <div className="sidebar-footer">
          <button onClick={handleLogout} className="btn btn-danger">
            Logout
          </button>
        </div>
      </nav>
      <main className="main-content">
        <Outlet />
      </main>
      
      <SuspensionModal
        isOpen={suspensionModal.isOpen}
        status={suspensionModal.status}
        message={suspensionModal.message}
        endDate={suspensionModal.endDate}
        onClose={handleCloseSuspensionModal}
      />
    </div>
  );
};

export default Dashboard;

